import { useCallback, useId, useRef, useState } from "react";
import type { TournamentGameDto } from "../../../../types/tournament-games";
import { setGamesPitchers } from "../../../../services/tournament-games";
import { Checkbox } from "../../../../components/Checkbox";
import { useDismiss } from "./useDismiss";
import { pitcherCandidates, pitcherIds, toggleId } from "./gamePitchers";
import { formatMatchup } from "./gameDisplay";

interface GamePitchersRowProps {
  calendarEventId: string;
  game: TournamentGameDto;
  /**
   * Receives this game, fresh from the save. Only this game is merged: the response's other games
   * are a snapshot that can be older than a save another row just made, so replacing the whole list
   * would undo it (and the next toggle there would then save the stale set).
   */
  onSaved: (updated: TournamentGameDto) => void;
}

/**
 * The "Pitching:" line on a tournament game: who is marked to pitch, and a checkbox list to change
 * it. Each toggle saves right away. The newest change wins and a failed save reverts to what the
 * server last had, the same way the coach picker on the row behaves.
 */
export const GamePitchersRow = ({ calendarEventId, game, onSaved }: GamePitchersRowProps) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const wrapRef = useDismiss<HTMLDivElement>(open, close);
  const listId = useId();
  const legendId = useId();
  // Optimistic selection while a save is in flight; null falls back to the saved game.
  const [pending, setPending] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const reqSeq = useRef(0);

  const candidates = pitcherCandidates(game);
  const selected = pending ?? pitcherIds(game);
  const nameById = new Map(candidates.map((p) => [p.prospectId, p.name]));
  const shown = selected.filter((id) => nameById.has(id));

  const handleToggle = async (prospectId: string, on: boolean) => {
    const next = toggleId(selected, prospectId, on);
    const seq = ++reqSeq.current;
    setPending(next);
    setSaving(true);
    setError("");
    try {
      const resp = await setGamesPitchers(calendarEventId, [
        { gameId: game.id, prospectIds: next },
      ]);
      if (seq !== reqSeq.current) return; // a newer change superseded this one
      const fresh = resp.games.games.find((g) => g.id === game.id);
      if (fresh) onSaved(fresh);
      setPending(null);
    } catch {
      if (seq !== reqSeq.current) return;
      setPending(null);
      setError("Couldn't save pitchers. Try again.");
    } finally {
      if (seq === reqSeq.current) setSaving(false);
    }
  };

  return (
    <div className="game-row__pitching" ref={wrapRef} data-testid={`game-pitchers-${game.id}`}>
      <div className="game-row__following">
        <span className="game-row__following-label">Pitching:</span>
        {shown.length === 0 ? (
          <span className="game-row__following-label">Not set</span>
        ) : (
          shown.map((id) => (
            <span key={id} className="game-row__follow-chip">
              {nameById.get(id)}
            </span>
          ))
        )}
        <button
          type="button"
          className="game-row__edit-btn"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={`Edit pitchers for ${formatMatchup(game)}`}
          data-testid={`game-pitchers-edit-${game.id}`}
        >
          Edit pitchers
        </button>
        {/* Always mounted so screen readers announce the text when it appears. */}
        <span className="game-row__status" aria-live="polite">
          {saving ? "Saving…" : ""}
        </span>
      </div>

      {open && (
        <div id={listId} className="game-row__pitcher-list">
          {candidates.length === 0 ? (
            <p className="game-row__following-label">
              Follow players on these teams first, then you can mark who is pitching.
            </p>
          ) : (
            <div role="group" aria-labelledby={legendId} className="game-row__pitcher-options">
              <span id={legendId} className="game-row__following-label">
                Who is pitching this game?
              </span>
              {candidates.map((p) => (
                <Checkbox
                  key={p.prospectId}
                  label={p.name}
                  checked={selected.includes(p.prospectId)}
                  onChange={(e) => void handleToggle(p.prospectId, e.target.checked)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="event-editor__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};
